/**
 * Application Step Component
 *
 * Step 4: Configure space type and garage doors (for Full Room mode).
 * Garage doors are conditionally shown for garage, shed, and workshop space types.
 */

import React, { useMemo, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useEstimator } from '../../../context/EstimatorContext';
import { useToast } from '../../../context/ToastContext';
import { SPACE_TYPES, GARAGE_DOOR_PRESETS, GARAGE_DOOR_SPACE_TYPES, ROOM_PRESETS } from '../../../data/presets';
import { trackSpaceTypeSelected, trackGarageDoorToggled, trackPresetSelected } from '../../../utils/gtmTracking';
import {
  Car,
  Home,
  WashingMachine,
  Hammer,
  DoorOpen,
  Warehouse,
  Building2,
  LayoutGrid,
  Palette,
  Move,
  Grid3X3,
  ClipboardList,
  Bath,
  AlertCircle,
} from 'lucide-react';
import { Room3DViewer } from '../../ui/room3d';
import { DimensionInput, UnitToggle } from '../../ui/DimensionInput';
import { useUnitMode } from '../../../hooks/useUnitMode';
import './Steps.css';

// Map icon names to components
const iconMap: Record<string, typeof Car> = {
  Car,
  Home,
  WashingMachine,
  Hammer,
  DoorOpen,
  Warehouse,
  Building2,
  LayoutGrid,
  Bath,
};

export const ApplicationStep: React.FC = () => {
  const { state, updateApplicationConfig, validateGarageDoors } = useEstimator();
  const { showToast } = useToast();
  const { t } = useTranslation('wizard');
  const { applicationConfig, dimensionsConfig, calculationMode, wallsConfig, ceilingConfig } = state;
  // One ft/in <-> decimal toggle for the garage door width/height group.
  const [garageUnitMode, setGarageUnitMode] = useUnitMode('app-garage');

  // Get current room dimensions
  const roomDimensions = useMemo(() => {
    if (dimensionsConfig.usePreset && dimensionsConfig.presetId) {
      const preset = ROOM_PRESETS.find(p => p.id === dimensionsConfig.presetId);
      if (preset) {
        return { length: preset.length, width: preset.width, height: preset.height };
      }
    }
    return dimensionsConfig.customDimensions || { length: 20, width: 12, height: 10 };
  }, [dimensionsConfig]);

  // Check if space type supports garage doors
  const supportsGarageDoors = useMemo(() => {
    return GARAGE_DOOR_SPACE_TYPES.includes(applicationConfig.spaceType as typeof GARAGE_DOOR_SPACE_TYPES[number]);
  }, [applicationConfig.spaceType]);

  // Only show garage doors section for FullRoom mode
  const showGarageDoors = calculationMode === 'FullRoom' && supportsGarageDoors;

  // Validation for garage doors
  const garageDoorValidation = useMemo(() => {
    if (!applicationConfig.garage.enabled) {
      return { isValid: true, errors: [] as string[] };
    }

    const errors: string[] = [];
    const { width, height, numDoors, onWallType } = applicationConfig.garage;

    // Get the wall length based on location
    const wallLength = onWallType === 'LengthWall' ? roomDimensions.length : roomDimensions.width;
    const wallHeight = roomDimensions.height;

    // Check if door height exceeds wall height
    if (height > wallHeight) {
      errors.push(t('application.garageDoors.errorDoorHeight', { height, wallHeight }));
    }

    // Check if total door width exceeds wall length
    const totalDoorWidth = width * numDoors;
    if (totalDoorWidth > wallLength) {
      errors.push(t('application.garageDoors.errorTotalWidth', { total: totalDoorWidth, wallLength }));
    }

    // Check individual door width
    if (width > wallLength) {
      errors.push(t('application.garageDoors.errorDoorWidth', { width, wallLength }));
    }

    return { isValid: errors.length === 0, errors };
  }, [applicationConfig.garage, roomDimensions, t]);

  // Get step number for tracking (application step position depends on mode)
  const applicationStepNumber = calculationMode === 'FullRoom' ? 5 : 4;

  // Confirmation modal state for switching away from garage-door-enabled space type
  const [pendingSpaceType, setPendingSpaceType] = useState<string | null>(null);

  const handleSpaceTypeChange = (spaceType: string) => {
    const newSupportsGarageDoors = GARAGE_DOOR_SPACE_TYPES.includes(spaceType as typeof GARAGE_DOOR_SPACE_TYPES[number]);

    // If garage doors are configured and switching to a type that doesn't support them, confirm first
    if (!newSupportsGarageDoors && applicationConfig.garage.enabled) {
      setPendingSpaceType(spaceType);
      return;
    }

    // Garage doors persist across garage-door-enabled types — just change space type
    updateApplicationConfig({ spaceType });
    trackSpaceTypeSelected(calculationMode, spaceType, applicationStepNumber);
  };

  const confirmSpaceTypeChange = () => {
    if (!pendingSpaceType) return;
    updateApplicationConfig({
      spaceType: pendingSpaceType,
      garage: { ...applicationConfig.garage, enabled: false, onWallType: 'None' },
    });
    trackSpaceTypeSelected(calculationMode, pendingSpaceType, applicationStepNumber);
    setPendingSpaceType(null);
  };

  const cancelSpaceTypeChange = () => {
    setPendingSpaceType(null);
  };

  const handleGarageToggle = (enabled: boolean) => {
    if (enabled) {
      // When enabling, validate existing dimensions against wall to ensure valid state
      const newOnWallType = applicationConfig.garage.onWallType === 'None' ? 'LengthWall' : applicationConfig.garage.onWallType;
      const wallLength = newOnWallType === 'LengthWall' ? roomDimensions.length : roomDimensions.width;
      const validated = validateGarageDoors(
        wallLength,
        roomDimensions.height,
        applicationConfig.garage.width,
        applicationConfig.garage.height,
        applicationConfig.garage.numDoors
      );
      updateApplicationConfig({
        garage: {
          ...applicationConfig.garage,
          enabled: true,
          onWallType: newOnWallType,
          width: validated.validWidth,
          height: validated.validHeight,
          // Preserve numDoors if valid, otherwise use validated value or fallback to 1
          numDoors: validated.validNumDoors > 0 ? validated.validNumDoors : (applicationConfig.garage.numDoors || 1),
        },
      });
    } else {
      // When disabling, preserve all settings including numDoors
      updateApplicationConfig({
        garage: {
          ...applicationConfig.garage,
          enabled: false,
          onWallType: 'None',
        },
      });
    }
    trackGarageDoorToggled(calculationMode, enabled, applicationStepNumber);
  };

  const handleDoorPresetSelect = (preset: typeof GARAGE_DOOR_PRESETS[number]) => {
    // Validate against wall dimensions
    const wallLength = applicationConfig.garage.onWallType === 'LengthWall' ? roomDimensions.length : roomDimensions.width;
    const validated = validateGarageDoors(
      wallLength,
      roomDimensions.height,
      preset.width,
      preset.height,
      applicationConfig.garage.numDoors
    );

    updateApplicationConfig({
      garage: {
        ...applicationConfig.garage,
        width: validated.validWidth,
        height: validated.validHeight,
        numDoors: validated.validNumDoors || 1,
      },
    });
    trackPresetSelected(calculationMode, preset.label, 'garage_door_size', applicationStepNumber);
  };

  const handleCustomDimensionChange = (field: 'width' | 'height', value: number) => {
    const wallLength = applicationConfig.garage.onWallType === 'LengthWall' ? roomDimensions.length : roomDimensions.width;
    const newWidth = field === 'width' ? value : applicationConfig.garage.width;
    const newHeight = field === 'height' ? value : applicationConfig.garage.height;

    const validated = validateGarageDoors(
      wallLength,
      roomDimensions.height,
      newWidth,
      newHeight,
      applicationConfig.garage.numDoors
    );

    updateApplicationConfig({
      garage: {
        ...applicationConfig.garage,
        width: validated.validWidth,
        height: validated.validHeight,
        numDoors: validated.validNumDoors,
      },
    });
  };

  const handleNumDoorsChange = (numDoors: number) => {
    const wallLength = applicationConfig.garage.onWallType === 'LengthWall' ? roomDimensions.length : roomDimensions.width;
    const validated = validateGarageDoors(
      wallLength,
      roomDimensions.height,
      applicationConfig.garage.width,
      applicationConfig.garage.height,
      numDoors
    );

    updateApplicationConfig({
      garage: {
        ...applicationConfig.garage,
        numDoors: validated.validNumDoors,
      },
    });
  };

  const handleWallTypeChange = (onWallType: 'LengthWall' | 'WidthWall') => {
    // Re-validate when wall type changes
    const wallLength = onWallType === 'LengthWall' ? roomDimensions.length : roomDimensions.width;
    const validated = validateGarageDoors(
      wallLength,
      roomDimensions.height,
      applicationConfig.garage.width,
      applicationConfig.garage.height,
      applicationConfig.garage.numDoors
    );

    updateApplicationConfig({
      garage: {
        ...applicationConfig.garage,
        onWallType,
        width: validated.validWidth,
        height: validated.validHeight,
        numDoors: validated.validNumDoors,
      },
    });
  };

  return (
    <div className="step-content">
      <div className="step-header">
        <h2 className="step-title">{t('application.title')}</h2>
        <p className="step-description">
          {t('application.description')}
        </p>
      </div>

      {/* Space Type Selection */}
      <div className="form-section">
        <h3 className="section-title">
          <Building2 size={20} className="section-icon" />
          {t('application.spaceTypeSection')}
        </h3>
        <div className="d-flex flex-wrap justify-content-center gap-2">
          {SPACE_TYPES.map(type => {
            const IconComponent = iconMap[type.icon];
            return (
              <button
                key={type.id}
                type="button"
                className={`icon-select-btn icon-select-sm ${
                  applicationConfig.spaceType === type.id ? 'selected' : ''
                }`}
                onClick={() => handleSpaceTypeChange(type.id)}
              >
                <div className="icon-select-img">
                  {IconComponent && <IconComponent size={32} strokeWidth={1.5} />}
                </div>
                <span className="icon-select-label">{t(`spaceTypes.${type.id}`)}</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Garage Doors Section - Only for FullRoom mode with garage/shed/workshop */}
      {showGarageDoors && (
        <div className="form-section garage-doors-section text-center">
          <h3 className="section-title justify-content-center">
            <DoorOpen size={20} className="section-icon" />
            {t('application.garageDoors.section')}
          </h3>
          <p className="text-secondary small mb-3">{t('application.garageDoors.intro')}</p>

          {/* Toggle */}
          <div className="toggle-row justify-content-center">
            <label className="toggle-switch">
              <input
                type="checkbox"
                checked={applicationConfig.garage.enabled}
                onChange={(e) => handleGarageToggle(e.target.checked)}
              />
              <span className="toggle-slider"></span>
            </label>
            <span className="toggle-label">{t('application.garageDoors.toggleLabel')}</span>
          </div>

          {applicationConfig.garage.enabled && (
            <>
              {/* Door Size Presets — full width above the cards */}
              <div className="form-group text-center mb-4">
                <label className="form-label">{t('application.garageDoors.presetsLabel')}</label>
                <div className="door-presets-grid justify-content-center">
                  {GARAGE_DOOR_PRESETS.map((preset, index) => {
                    const wallLength = applicationConfig.garage.onWallType === 'LengthWall' ? roomDimensions.length : roomDimensions.width;
                    const doesNotFit = preset.width > wallLength || preset.height > roomDimensions.height;
                    const isSelected = applicationConfig.garage.width === preset.width &&
                      applicationConfig.garage.height === preset.height;
                    return (
                      <button
                        key={index}
                        type="button"
                        className={`door-preset-btn ${isSelected ? 'selected' : ''} ${doesNotFit ? 'disabled' : ''}`}
                        onClick={() => {
                          if (doesNotFit) {
                            showToast(t('application.garageDoors.doesNotFit'));
                          } else {
                                                        handleDoorPresetSelect(preset);
                          }
                        }}
                        title={doesNotFit
                          ? t('application.garageDoors.doesNotFitTitle', {
                              doorWidth: preset.width,
                              doorHeight: preset.height,
                              wallLength,
                              wallHeight: roomDimensions.height,
                            })
                          : t(`application.garageDoors.preset.${preset.sizeClass}`, { width: preset.width, height: preset.height })}
                      >
                        {t(`application.garageDoors.preset.${preset.sizeClass}`, { width: preset.width, height: preset.height })}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Three config cards in a responsive row */}
              <div className="row g-3 justify-content-center">
                {/* Custom Size Card */}
                <div className="col-12 col-md-6 col-xl-4">
                  <div className="card h-100 border-light">
                    <div className="card-body text-center">
                      <label className="form-label fw-semibold">{t('application.garageDoors.customSize')}</label>
                      <div className="door-custom-inputs justify-content-center">
                        <div className="input-with-label">
                          <span className="input-label-text">{t('application.garageDoors.width')}</span>
                          <DimensionInput
                            value={applicationConfig.garage.width}
                            onChange={(v) => handleCustomDimensionChange('width', v)}
                            mode={garageUnitMode}
                            min={1}
                            max={applicationConfig.garage.onWallType === 'LengthWall' ? roomDimensions.length : roomDimensions.width}
                            hasError={applicationConfig.garage.width < 1 || applicationConfig.garage.width > (applicationConfig.garage.onWallType === 'LengthWall' ? roomDimensions.length : roomDimensions.width)}
                            fieldId="garage-width"
                            ariaLabel={t('application.garageDoors.widthAria')}
                          />
                          <span className="input-constraint">{t('application.garageDoors.max', { value: applicationConfig.garage.onWallType === 'LengthWall' ? roomDimensions.length : roomDimensions.width })}</span>
                        </div>
                        <div className="input-with-label">
                          <span className="input-label-text">{t('application.garageDoors.height')}</span>
                          <DimensionInput
                            value={applicationConfig.garage.height}
                            onChange={(v) => handleCustomDimensionChange('height', v)}
                            mode={garageUnitMode}
                            min={1}
                            max={roomDimensions.height}
                            hasError={applicationConfig.garage.height < 1 || applicationConfig.garage.height > roomDimensions.height}
                            fieldId="garage-height"
                            ariaLabel={t('application.garageDoors.heightAria')}
                          />
                          <span className="input-constraint">{t('application.garageDoors.max', { value: roomDimensions.height })}</span>
                        </div>
                      </div>
                      <UnitToggle mode={garageUnitMode} onChange={setGarageUnitMode} />
                    </div>
                  </div>
                </div>

                {/* Number of Doors Card */}
                <div className="col-12 col-md-6 col-xl-4">
                  <div className="card h-100 border-light">
                    <div className="card-body text-center">
                      <label className="form-label fw-semibold">{t('application.garageDoors.numDoors')}</label>
                      <div className="num-doors-selector justify-content-center">
                        {[1, 2, 3, 4].map((num) => {
                          const wallLength = applicationConfig.garage.onWallType === 'LengthWall' ? roomDimensions.length : roomDimensions.width;
                          const invalidSize = applicationConfig.garage.width < 1 || applicationConfig.garage.width > wallLength || applicationConfig.garage.height < 1 || applicationConfig.garage.height > roomDimensions.height;
                          const wouldExceed = applicationConfig.garage.width * num > wallLength || invalidSize;
                          return (
                            <button
                              key={num}
                              type="button"
                              className={`num-door-btn ${applicationConfig.garage.numDoors === num && !wouldExceed ? 'selected' : ''} ${wouldExceed ? 'disabled' : ''}`}
                              onClick={() => {
                                if (wouldExceed) {
                                  showToast(t('application.garageDoors.doesNotFit'));
                                } else {
                                                                    handleNumDoorsChange(num);
                                }
                              }}
                              title={wouldExceed
                                ? (invalidSize
                                    ? t('application.garageDoors.fixSizeFirst')
                                    : t('application.garageDoors.numDoorsExceeds', { count: num, total: applicationConfig.garage.width * num, wallLength }))
                                : t('application.garageDoors.doorCount', { count: num })}
                            >
                              {num}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Door Location Card */}
                <div className="col-12 col-md-6 col-xl-4">
                  <div className="card h-100 border-light">
                    <div className="card-body text-center">
                      <label className="form-label fw-semibold">{t('application.garageDoors.location')}</label>
                      <div className="door-location-selector justify-content-center">
                        <button
                          type="button"
                          className={`door-location-btn ${applicationConfig.garage.onWallType === 'LengthWall' ? 'selected' : ''}`}
                          onClick={() => handleWallTypeChange('LengthWall')}
                        >
                          <strong>{t('application.garageDoors.lengthWall', { length: roomDimensions.length })}</strong>
                          <span className="location-subtitle">{t('application.garageDoors.lengthWallSubtitle')}</span>
                        </button>
                        <button
                          type="button"
                          className={`door-location-btn ${applicationConfig.garage.onWallType === 'WidthWall' ? 'selected' : ''}`}
                          onClick={() => handleWallTypeChange('WidthWall')}
                        >
                          <strong>{t('application.garageDoors.widthWall', { width: roomDimensions.width })}</strong>
                          <span className="location-subtitle">{t('application.garageDoors.widthWallSubtitle')}</span>
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Validation Errors */}
              {!garageDoorValidation.isValid && (
                <div className="validation-errors mt-3">
                  {garageDoorValidation.errors.map((error, index) => (
                    <div key={index} className="validation-error">
                      <AlertCircle size={16} />
                      <span>{error}</span>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* 3D Room Preview */}
      {calculationMode === 'FullRoom' && (
        <Room3DViewer
          length={roomDimensions.length}
          width={roomDimensions.width}
          height={roomDimensions.height}
          showHeight={true}
          highlightWalls={true}
          wallColor={wallsConfig.color}
          ceilingColor={ceilingConfig.color}
          wallOrientation={wallsConfig.orientation}
          showCeiling={ceilingConfig.include}
          slatWallConfig={wallsConfig.slatWallNumPanels > 0 ? {
            enabled: true,
            startHeight: wallsConfig.slatWallStartHeight,
            numPanelRows: wallsConfig.slatWallNumPanels,
            color: wallsConfig.slatWallColor,
          } : null}
          garageConfig={applicationConfig.garage.enabled ? {
            enabled: true,
            width: applicationConfig.garage.width,
            height: applicationConfig.garage.height,
            numDoors: applicationConfig.garage.numDoors,
            onWallType: applicationConfig.garage.onWallType !== 'None' ? applicationConfig.garage.onWallType : 'LengthWall',
          } : null}
        />
      )}

      {/* Summary Preview */}
      <div className="summary-preview">
        <h3 className="section-title">
          <ClipboardList size={20} className="section-icon" />
          {t('application.summary.title')}
        </h3>
        <ul className="summary-list">
          {calculationMode === 'IndividualWalls' && state.individualWalls ? (
            <>
              <li>
                <Palette size={16} className="summary-icon" />
                <span className="summary-label">{t('application.summary.walls')}</span>
                <span className="summary-value">
                  {t('application.summary.wallsConfigured', { count: state.individualWalls.filter(w => w.enabled).length })}
                </span>
              </li>
              {(() => {
                const colors = [...new Set(state.individualWalls.filter(w => w.enabled).map(w => w.color))];
                const orientations = [...new Set(state.individualWalls.filter(w => w.enabled).map(w => w.orientation))];
                return (
                  <>
                    <li>
                      <Palette size={16} className="summary-icon" />
                      <span className="summary-label">{t('application.summary.colors')}</span>
                      <span className="summary-value">{colors.join(', ') || t('common:notAvailable')}</span>
                    </li>
                    <li>
                      <Move size={16} className="summary-icon" />
                      <span className="summary-label">{t('application.summary.orientations')}</span>
                      <span className="summary-value">{orientations.join(', ') || t('common:notAvailable')}</span>
                    </li>
                  </>
                );
              })()}
              {state.individualWalls.some(w => w.enabled && w.slatWall.enabled) && (
                <li>
                  <Grid3X3 size={16} className="summary-icon" />
                  <span className="summary-label">{t('application.summary.slatWall')}</span>
                  <span className="summary-value">
                    {t('application.summary.slatWallWalls', { count: state.individualWalls.filter(w => w.enabled && w.slatWall.enabled).length })}
                  </span>
                </li>
              )}
              {(() => {
                const wallsWithGarage = state.individualWalls!.filter(w => w.enabled && w.garage.enabled && w.garage.numDoors > 0);
                if (wallsWithGarage.length === 0) return null;
                return wallsWithGarage.map(w => (
                  <li key={`garage-${w.wallId}`}>
                    <Car size={16} className="summary-icon" />
                    <span className="summary-label">{t('application.summary.garageDoors')}</span>
                    <span className="summary-value">
                      {t('application.summary.garageDoorsOnWall', { count: w.garage.numDoors, width: w.garage.width, height: w.garage.height, wall: w.name })}
                    </span>
                  </li>
                ));
              })()}
            </>
          ) : (
            <>
              <li>
                <Palette size={16} className="summary-icon" />
                <span className="summary-label">{t('application.summary.wallColor')}</span>
                <span className="summary-value">{state.wallsConfig.color}</span>
              </li>
              <li>
                <Move size={16} className="summary-icon" />
                <span className="summary-label">{t('application.summary.orientation')}</span>
                <span className="summary-value">{state.wallsConfig.orientation}</span>
              </li>
              {state.wallsConfig.slatWallNumPanels > 0 && (
                <li>
                  <Grid3X3 size={16} className="summary-icon" />
                  <span className="summary-label">{t('application.summary.slatWall')}</span>
                  <span className="summary-value">
                    {t('application.summary.slatWallPanels', { count: state.wallsConfig.slatWallNumPanels, feet: (state.wallsConfig.slatWallNumPanels * 0.5).toFixed(1), color: state.wallsConfig.slatWallColor })}
                  </span>
                </li>
              )}
            </>
          )}
          <li>
            <Home size={16} className="summary-icon" />
            <span className="summary-label">{t('application.summary.ceiling')}</span>
            <span className="summary-value">
              {state.ceilingConfig.include
                ? (() => {
                    const zones = state.ceilingConfig.zones || [];
                    if (zones.length > 0) {
                      const colors = [...new Set(zones.map(z => z.color))];
                      return colors.join(', ');
                    }
                    return state.ceilingConfig.color;
                  })()
                : t('application.summary.ceilingNotIncluded')
              }
            </span>
          </li>
          <li>
            {(() => {
              const spaceType = SPACE_TYPES.find(st => st.id === applicationConfig.spaceType);
              const SpaceIcon = spaceType ? iconMap[spaceType.icon] : Building2;
              return (
                <>
                  {SpaceIcon && <SpaceIcon size={16} className="summary-icon" />}
                  <span className="summary-label">{t('application.summary.spaceType')}</span>
                  <span className="summary-value">{spaceType ? t(`spaceTypes.${spaceType.id}`) : ''}</span>
                </>
              );
            })()}
          </li>
          {calculationMode === 'FullRoom' && applicationConfig.garage.enabled && (
            <li>
              <Car size={16} className="summary-icon" />
              <span className="summary-label">{t('application.summary.garageDoors')}</span>
              <span className="summary-value">
                {t('application.summary.garageDoorsValue', { count: applicationConfig.garage.numDoors, width: applicationConfig.garage.width, height: applicationConfig.garage.height })}
                {applicationConfig.garage.onWallType !== 'None' && (
                  <> {applicationConfig.garage.onWallType === 'LengthWall' ? t('application.summary.onLengthWall') : t('application.summary.onWidthWall')}</>
                )}
              </span>
            </li>
          )}
        </ul>
      </div>
      {/* Confirmation modal for losing garage door config */}
      {pendingSpaceType && (
        <div className="modal-overlay" style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999,
        }}>
          <div style={{
            background: '#fff', borderRadius: '12px', padding: '24px 28px',
            maxWidth: '420px', width: '90%', boxShadow: '0 8px 32px rgba(0,0,0,0.2)',
            textAlign: 'center',
          }}>
            <AlertCircle size={36} style={{ color: '#e67e22', marginBottom: '12px' }} />
            <h4 style={{ margin: '0 0 8px', fontSize: '1.1rem' }}>{t('application.changeSpaceType.title')}</h4>
            <p style={{ margin: '0 0 20px', color: '#666', fontSize: '0.9rem', lineHeight: 1.5 }}>
              {/* Trans keeps the <strong> inside the sentence, so translators can
                  move the emphasised space-type name wherever their grammar needs it. */}
              <Trans
                i18nKey="application.changeSpaceType.body"
                ns="wizard"
                values={{ spaceType: t(`spaceTypes.${pendingSpaceType}`) }}
                components={{ b: <strong /> }}
              />
            </p>
            <div style={{ display: 'flex', gap: '10px', justifyContent: 'center' }}>
              <button
                type="button"
                className="btn btn-secondary"
                onClick={cancelSpaceTypeChange}
                style={{ padding: '8px 20px', fontSize: '0.85rem', fontWeight: 600 }}
              >
                {t('application.changeSpaceType.cancel')}
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={confirmSpaceTypeChange}
                style={{ padding: '8px 20px', fontSize: '0.85rem', fontWeight: 600, background: '#e67e22', borderColor: '#e67e22' }}
              >
                {t('application.changeSpaceType.confirm')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ApplicationStep;
